import { FC } from 'react';
import { useLocation } from 'react-router-dom';
import { useDesktop } from '@utils/hooks/useDesktop';
import { usePaulina } from './PaulinaProvider';
import { usePaulinaVersionControls } from './usePaulinaVersionControls';
import { usePaulinaTipGate } from './usePaulinaTipGate';
import type { Nudge } from './usePaulinaChat';
import { PaulinaPill } from './PaulinaPill';
import { PaulinaPanel } from './PaulinaPanel';
import { PaulinaSheet } from './PaulinaSheet';
import { PaulinaNudgeBubble } from './PaulinaNudgeBubble';
import { PaulinaWelcomeCoachmark } from './PaulinaWelcomeCoachmark';

/**
 * AgentChatWidget — host de la entrada de Paulina (Opción A). Orquesta las superficies por VIEWPORT.
 *
 *   DESKTOP → PaulinaPill (entrada nombrada, z-80; NUNCA se oculta: cede colapsando a solo-avatar y
 *             reubicándose abajo-izquierda cuando hay pedido/detalle) + PaulinaPanel (chat abierto,
 *             z-95, cede ante el toast) + PaulinaNudgeBubble (proactividad, ruteada por OverlayGate).
 *   MÓVIL   → PaulinaSheet (bottom-sheet, z-95). La ENTRADA en móvil es el avatar de Paulina en
 *             MobileTopBarV4 (header), que abre este sheet vía el contexto y NUNCA se oculta.
 *
 * COEXISTIR, NO OCULTAR (Track 1): la entrada de Paulina no desaparece en ninguna resolución. En
 * desktop la pill cede (compacta + reubicada), en móvil el avatar del header queda siempre visible;
 * pero NUNCA tapan el toast de pedido (z-99) ni las acciones del detalle (drawer /detalle/, z-3).
 *
 * ROLLOUT (feature agent-chat) — reparto: la INVITACIÓN ("Conoce a Paulina") y el PROMPT de feedback
 * al desactivar viven a nivel Layout (PaulinaInviteHost / PaulinaRolloutOverlays), FUERA del
 * PaulinaGate. DESACTIVAR: el control del header (overflow) lo cablea este host vía
 * usePaulinaVersionControls (`deactivate` = acción o null: root efectivo + gate del contrato).
 *
 * Toda la lógica del chat (polling, historial, nudges, telemetría pau_*, navegación, aislamiento
 * multi-tenant) vive en `usePaulinaChat` (compartida vía PaulinaProvider); la cesión ante pedidos
 * (`hayPedido`) viene de `usePaulinaShouldYield`. Lo operativo SIEMPRE gana.
 */

// Host de la speech-bubble proactiva de desktop, ruteada por el OverlayGate (usePaulinaTipGate): un
// componente propio para que el hook del slot se llame de forma estable (no tras el early-return de
// viewport de AgentChatWidget). Renderiza la bubble solo cuando el gate la concede (cede ante welcome,
// OS-nags, anuncios y pedido) y marca el tip como mostrado al pintarse.
const PaulinaNudgeBubbleHost: FC<{
  nudge: Nudge | null;
  eligible: boolean;
  onAccept: (n: Nudge) => void;
  onDismiss: (n: Nudge) => void;
}> = ({ nudge, eligible, onAccept, onDismiss }) => {
  const show = usePaulinaTipGate(nudge, eligible);
  if (!show || !nudge) return null;
  return <PaulinaNudgeBubble nudge={nudge} onAccept={onAccept} onDismiss={onDismiss} />;
};

export const AgentChatWidget: FC = () => {
  const isDesktop = useDesktop();
  const paulina = usePaulina();
  const { deactivate } = usePaulinaVersionControls();
  const { pathname } = useLocation();

  if (!paulina || !paulina.enabled) return null;

  const {
    open,
    setOpen,
    turns,
    input,
    setInput,
    busy,
    error,
    nudge,
    sesionChatId,
    bottomRef,
    toggleOpen,
    goTo,
    aceptarNudge,
    descartarNudge,
    clearConversation,
    send,
    doSend,
    hayPedido,
  } = paulina;

  // Solo ofrecemos "Desactivar Paulina" si el contrato entregó la acción (root efectivo + la
  // feature sigue siendo gestionable); si es null, el overflow del header ni se monta.
  const onDeactivate = deactivate ?? undefined;

  // Operational UI wins: Paulina yields on an incoming order OR when the order detail drawer
  // (/detalle/:orderId, z-3) is open, so it never covers the detail actions (e.g. Cancel order).
  const shouldYield = hayPedido || pathname.includes('/detalle/');

  // MÓVIL: la entrada es el avatar del header (MobileTopBarV4), que ya NO se oculta. Con el chat
  // cerrado, aquí solo puede aparecer el coachmark de bienvenida "una sola vez", anclado bajo el
  // avatar y suprimido si hay pedido/detalle (shouldYield); el OverlayGate además lo coordina.
  if (!isDesktop) {
    if (!open) return !shouldYield ? <PaulinaWelcomeCoachmark /> : null;
    return (
      <PaulinaSheet
        turns={turns}
        input={input}
        setInput={setInput}
        busy={busy}
        error={error}
        bottomRef={bottomRef}
        goTo={goTo}
        send={send}
        doSend={doSend}
        setOpen={setOpen}
        clearConversation={clearConversation}
        sesionChatId={sesionChatId}
        nudge={nudge}
        aceptarNudge={aceptarNudge}
        descartarNudge={descartarNudge}
        onDeactivate={onDeactivate}
      />
    );
  }

  // DESKTOP: pill (entrada, SIEMPRE montada — cede sin ocultarse) + panel (chat) + bubble (proactividad).
  return (
    <>
      {open ? (
        <PaulinaPanel
          turns={turns}
          input={input}
          setInput={setInput}
          busy={busy}
          error={error}
          bottomRef={bottomRef}
          goTo={goTo}
          send={send}
          doSend={doSend}
          setOpen={setOpen}
          clearConversation={clearConversation}
          sesionChatId={sesionChatId}
          hayPedido={hayPedido}
          onDeactivate={onDeactivate}
        />
      ) : (
        // Coexiste, no se oculta: en pedido/detalle la pill CEDE (yielding) — colapsa a solo-avatar y
        // se reubica abajo-izquierda, fuera del toast (abajo-derecha) y del detalle.
        <PaulinaPill hasNudge={Boolean(nudge)} yielding={shouldYield} onOpen={toggleOpen} />
      )}

      {/* Proactive speech-bubble over the pill, routed through the OverlayGate (yields to welcome,
          OS nags, announcements and an incoming order; suppressed when the chat is open). */}
      <PaulinaNudgeBubbleHost
        nudge={nudge}
        eligible={!open && !shouldYield}
        onAccept={aceptarNudge}
        onDismiss={descartarNudge}
      />
    </>
  );
};
